import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useEffect, useMemo, useState } from "react";
import { ExternalLink, RefreshCw, Send } from "lucide-react";
import type { ExternalResourceLink, IntegrationDeliveryStatus } from "@io-spoke/contracts/integrations";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  exportEntityToIntegration,
  getEntityIntegrationStatus,
  listIntegrationConnections,
  retryIntegrationDelivery,
  type IntegrationConnection,
} from "../../lib/integrations";

type Props = {
  entityId: string;
  entityStatus: string;
  canManageIntegrations: boolean;
  navigate: (route: string) => void;
};

const deliveryLabels: Record<IntegrationDeliveryStatus["status"], string> = {
  pending: "queued", processing: "in progress", completed: "complete", failed: "failed",
};

export function getSafeAzureDevOpsUrl(value: string | null) {
  if (!value) return null;
  try {
    const url = new URL(value);
    const isAzureDevOpsHost =
      url.hostname === "dev.azure.com" || url.hostname.endsWith(".visualstudio.com");
    return url.protocol === "https:" && isAzureDevOpsHost ? url.toString() : null;
  } catch {
    return null;
  }
}

export function EntityIntegrationExportCard({
  entityId,
  entityStatus,
  canManageIntegrations,
  navigate,
}: Props) {
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [links, setLinks] = useState<ExternalResourceLink[]>([]);
  const [deliveries, setDeliveries] = useState<IntegrationDeliveryStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const exporting = useAsyncAction();
  const retrying = useAsyncAction();
  const isExporting = exporting.pending || retrying.pending;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const isApprovedWork = !["new", "proposed"].includes(entityStatus);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError(null);
    Promise.all([
      canManageIntegrations ? listIntegrationConnections() : Promise.resolve([]),
      getEntityIntegrationStatus(entityId),
    ])
      .then(([connectionList, integrationStatus]) => {
        if (!isActive) return;
        const activeConnections = connectionList.filter(
          (connection) => connection.provider === "azure_devops" && connection.status === "active"
        );
        setConnections(activeConnections);
        setLinks(integrationStatus.links);
        setDeliveries(integrationStatus.deliveries);
      })
      .catch((loadError) => {
        if (!isActive) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load Azure DevOps export details.");
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => { isActive = false; };
  }, [canManageIntegrations, entityId]);

  const selectedLink = links[0];
  const latestDelivery = deliveries[0];
  const automaticConnection = useMemo(() => {
    if (connections.length === 1) return connections[0];
    return connections.find((connection) => connection.isDefault);
  }, [connections]);
  const safeExternalUrl = getSafeAzureDevOpsUrl(selectedLink?.externalUrl ?? null);

  useEffect(() => {
    if (!latestDelivery || !["pending", "processing"].includes(latestDelivery.status)) return;
    let isActive = true;
    const timer = window.setInterval(() => {
      void getEntityIntegrationStatus(entityId).then((status) => {
        if (!isActive) return;
        setLinks(status.links);
        setDeliveries(status.deliveries);
      }).catch((pollError) => {
        if (isActive) setError(pollError instanceof Error ? pollError.message : "Unable to refresh delivery status.");
      });
    }, 2500);
    return () => {
      isActive = false;
      window.clearInterval(timer);
    };
  }, [entityId, latestDelivery?.id, latestDelivery?.status]);

  const handleExport = () => exporting.run(async () => {
    if (!automaticConnection) return;
    setError(null);
    setNotice(null);
    try {
      const result = await exportEntityToIntegration(automaticConnection.id, entityId);
      setLinks((current) => [result.link, ...current.filter((link) => link.id !== result.link.id)]);
      setNotice(result.deduped ? "This work item was already sent to Azure DevOps." : "Work item sent to Azure DevOps.");
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : "Unable to send this work item to Azure DevOps.");
    }
  });

  const handleRetry = () => retrying.run(async () => {
    if (!latestDelivery) return;
    setError(null);
    try {
      await retryIntegrationDelivery(latestDelivery.id);
      setDeliveries((current) => current.map((delivery) => delivery.id === latestDelivery.id
        ? { ...delivery, status: "pending", attemptCount: 0, errorMessage: null }
        : delivery));
      setNotice("Azure DevOps delivery queued for retry.");
    } catch (retryError) {
      setError(retryError instanceof Error ? retryError.message : "Unable to retry Azure DevOps delivery.");
    }
  });

  return (
    <Card className="border-border bg-card/60">
      <CardHeader>
        <CardTitle className="text-lg">Azure DevOps</CardTitle>
        <CardDescription>Approved work is delivered automatically to your configured engineering project.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <p className="text-sm text-muted-foreground">Loading Azure DevOps status…</p> : null}
        {!isLoading && !isApprovedWork ? (
          <p className="text-sm text-muted-foreground">Approve this work item to deliver it automatically to Azure DevOps.</p>
        ) : null}
        {!isLoading && isApprovedWork && !canManageIntegrations ? (
          <p className="text-sm text-muted-foreground">IO Spoke will show the Azure DevOps link here after automatic delivery.</p>
        ) : null}
        {!isLoading && isApprovedWork && canManageIntegrations && !selectedLink && !latestDelivery && connections.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">No active Azure DevOps connection is available.</p>
            <Button variant="outline" onClick={() => navigate("/settings/integrations")}>Configure integrations</Button>
          </div>
        ) : null}
        {!isLoading && isApprovedWork && canManageIntegrations && !selectedLink && !latestDelivery && connections.length > 1 && !automaticConnection ? (
          <div className="space-y-3"><p className="text-sm text-muted-foreground">Choose a default connection so IO Spoke knows where to deliver approved work.</p><Button variant="outline" onClick={() => navigate("/settings/integrations")}>Choose default connection</Button></div>
        ) : null}
        {latestDelivery && !selectedLink ? (
          <div className="rounded-md border border-border/60 bg-muted/40 p-4 text-sm">
            <p className="font-medium">Delivery {deliveryLabels[latestDelivery.status]}</p>
            <p className="mt-1 text-muted-foreground">Destination: {latestDelivery.connectionName}. Attempt {latestDelivery.attemptCount} of {latestDelivery.maxAttempts}.</p>
            {latestDelivery.errorMessage ? <p className="mt-2 text-red-400" role="alert">{latestDelivery.errorMessage}</p> : null}
            {latestDelivery.status === "failed" && canManageIntegrations ? <Button className="mt-3 min-h-11" variant="outline" onClick={handleRetry} disabled={isExporting} loading={retrying.pending} loadingLabel="Queueing…"><RefreshCw className="mr-2 h-4 w-4"/>Retry delivery</Button> : null}
          </div>
        ) : null}
        {!isLoading && isApprovedWork && canManageIntegrations && !selectedLink && !latestDelivery && automaticConnection ? (
          <div className="space-y-3"><p className="text-sm text-muted-foreground">This item predates automatic delivery. Send it through {automaticConnection.name} as a recovery action.</p><Button className="min-h-11" onClick={handleExport} disabled={isExporting} loading={exporting.pending} loadingLabel="Sending…"><Send className="mr-2 h-4 w-4" />Send now</Button></div>
        ) : null}
        {notice ? <p className="text-sm text-emerald-400" role="status">{notice}</p> : null}
        {error ? <p className="text-sm text-red-400" role="alert">{error}</p> : null}
        {selectedLink ? (
          <div className="rounded-md border border-border/60 bg-muted/40 p-4 text-sm">
            <p className="font-medium">Azure DevOps work item {selectedLink.externalId}</p>
            <p className="mt-1 text-muted-foreground">Connected through {selectedLink.connectionName}.</p>
            {safeExternalUrl ? (
              <a className="mt-3 inline-flex min-h-11 items-center text-primary underline underline-offset-4" href={safeExternalUrl} target="_blank" rel="noopener noreferrer">
                Open in Azure DevOps <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            ) : (
              <p className="mt-2 text-muted-foreground">The external item link is unavailable.</p>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
